import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Drawer, Dropdown, Menu, Message, Modal, Spin, Tooltip } from '@arco-design/web-react';
import { IconApps, IconCompass, IconDown, IconFile, IconLanguage, IconMenu } from '@arco-design/web-react/icon';

import { ConfigContext } from '@/components/ConfigProvider/configProvider';
import { useLeftMenu, useMenu } from '@/hooks/useMenu';
import { NAV_MAP } from '@/hooks/useMenu/constants';
import { getModule, getRouteScope } from '@/hooks/useRouteScope';

// 导入样式
import DropMenu from './DropMenu';
// 导入导航栏子组件
import { ChangeModeModal, persistConsoleMode, useModeState } from './components/ChangeMode';
import type { ConsoleMode } from './components/ChangeMode';
import ChangeModeContent from './components/ChangeMode';
import ChangeModule from './components/ChangeModule';
import DocBtn from './components/Doc';
import Explore, { EXPLORE_ITEMS } from './components/Explore';
import Logo from './components/Logo';
import SelectLang from './components/SelectLang';
import User from './components/User';
import styles from './styles/index.module.less';

const CONSOLE_TITLE_KEYS = {
  system: 'hooks:system',
  'data-center': 'common:datacenter',
  cluster: 'common:datacenter',
  console: 'common:tenant.space',
  wsp: 'common:tenant.space',
};

/** 取菜单跳转 path：优先 path，产品目录叶子回退到 all-skus */
function getMenuItemPath(item: any): string | undefined {
  if (item?.path) return item.path;
  // buildTree 在扁平数据带 children 时可能未写入 path，叶子用 id 回退
  if (item?.id && !(Array.isArray(item?.children) && item.children.length > 0)) {
    return `/product/all-skus?productCatalogueItemId=${item.id}`;
  }
  return undefined;
}
function resolveMenuLink(path?: string): { external?: string; to?: { pathname: string; search?: string } } | null {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return { external: path };
  const qIndex = path.indexOf('?');
  if (qIndex === -1) return { to: { pathname: path } };
  return { to: { pathname: path.slice(0, qIndex), search: path.slice(qIndex) } };
}

/** 移动端抽屉内菜单条目：递归展开，叶子节点用 Link 跳转 */
function MobileNavItem({ title, path, items, onClose }: { title: string; path?: string; items?: any[]; onClose: () => void }) {
  const [expanded, setExpanded] = useState(false);
  // 仅把「有子级」或「有 path」的节点算作可展示子项；空数组视为叶子
  const childItems = (items || []).filter((c) => (Array.isArray(c?.children) && c.children.length > 0) || !!getMenuItemPath(c));
  const hasChildren = childItems.length > 0;
  const link = !hasChildren ? resolveMenuLink(path || undefined) : null;

  if (link?.external) {
    return (
      <div className={styles.mobileNavItem}>
        <a className={styles.mobileNavItemBtn} href={link.external} target="_blank" rel="noreferrer" onClick={() => onClose()}>
          <span className={styles.mobileNavItemTitle}>{title}</span>
        </a>
      </div>
    );
  }

  if (link?.to) {
    return (
      <div className={styles.mobileNavItem}>
        <Link className={styles.mobileNavItemBtn} to={link.to} onClick={() => onClose()}>
          <span className={styles.mobileNavItemTitle}>{title}</span>
        </Link>
      </div>
    );
  }

  return (
    <div className={styles.mobileNavItem}>
      <button
        type="button"
        className={styles.mobileNavItemBtn}
        onClick={() => {
          if (hasChildren) setExpanded((v) => !v);
        }}
      >
        <span className={styles.mobileNavItemTitle}>{title}</span>
        {hasChildren && <IconDown className={`${styles.mobileNavItemArrow} ${expanded ? styles.mobileNavItemArrowOpen : ''}`} />}
      </button>
      {hasChildren && expanded && (
        <div className={styles.mobileNavItemChildren}>
          {childItems.map((child: any, idx: number) => (
            <MobileNavItem
              key={child.id || child.title || idx}
              title={NAV_MAP[child.title]?.title || child.title || child.name}
              path={getMenuItemPath(child)}
              items={child.children}
              onClose={onClose}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** 移动端通用图标按钮 */
function MobileIconBtn({
  icon,
  label,
  onClick,
  tooltip = true,
}: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  tooltip?: boolean;
}) {
  const button = (
    <button type="button" className={styles.mobileIconBtn} onClick={onClick} aria-label={label}>
      {icon}
    </button>
  );

  return tooltip ? (
    <Tooltip content={label} position="bottom">
      {button}
    </Tooltip>
  ) : (
    button
  );
}

/** 移动端语言切换图标按钮 */
function MobileLangBtn() {
  const { lang, setLang } = useContext(ConfigContext);

  const selectLang = (newLang: 'zh-cn' | 'en') => {
    if (lang === newLang) return;
    setLang(newLang);
    window.location.reload();
  };

  const dropList = (
    <Menu className={styles['lang-dropdown-menu']} selectedKeys={[lang]}>
      <Menu.Item key="en" onClick={() => selectLang('en')}>
        English
      </Menu.Item>
      <Menu.Item key="zh-cn" onClick={() => selectLang('zh-cn')}>
        简体中文
      </Menu.Item>
    </Menu>
  );

  return (
    <Dropdown droplist={dropList} trigger={['click']} position="bl">
      <div>
        <MobileIconBtn icon={<IconLanguage />} label="切换语言" tooltip={false} />
      </div>
    </Dropdown>
  );
}

/** 移动端文档图标按钮 */
function MobileDocBtn() {
  const { config } = useContext(ConfigContext);
  const { t } = useTranslation();

  if (!config?.doc) return null;

  return <MobileIconBtn icon={<IconFile />} label={t('common:userDoc')} onClick={() => window.open(config?.doc, '_blank')} />;
}

/** 移动端资源广场入口，与控制台、文档和语言同属全局工具栏。 */
function MobileExploreBtn() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dropList = (
    <Menu>
      {EXPLORE_ITEMS.map((item) => (
        <Menu.Item key={item.key} onClick={() => navigate(item.path)}>
          {t(item.labelKey)}
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Dropdown droplist={dropList} trigger={['click']} position="bl">
      <div>
        <MobileIconBtn icon={<IconCompass />} label={t('common:resource.marketplace')} tooltip={false} />
      </div>
    </Dropdown>
  );
}

/** 移动端控制台模式切换图标按钮 + 内联 Modal */
function MobileConsoleBtn() {
  const { setMode, isLogin } = useContext(ConfigContext);
  const { modeMap, loading } = useModeState();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  if (!isLogin) return null;

  const open = () => {
    if (loading) {
      setVisible(true);
      return;
    }
    const l = Object.keys(modeMap)?.length;
    if (!l) {
      Message.error(t('login-page:no.permissions.available.please.contact.administrator'));
      return;
    }
    if (l > 1 || modeMap.system) {
      setVisible(true);
    } else {
      const onlyMode = Object.keys(modeMap)[0] as ConsoleMode;
      setMode(onlyMode);
      persistConsoleMode(onlyMode);
      navigate(Object.values(modeMap)[0]);
    }
  };

  return (
    <>
      <MobileIconBtn icon={<IconApps />} label={t('common:console')} onClick={open} />
      <Modal
        className={styles.changeModeModal}
        title={
          <div className={styles.changeModeModalHeader}>
            <div className={styles.changeModeModalTitle}>{t('navbar:switch-management-space')}</div>
            <div className={styles.changeModeModalSubtitle}>{t('navbar:select-management-space')}</div>
          </div>
        }
        visible={visible}
        footer={null}
        onCancel={() => setVisible(false)}
        style={{ width: 760 }}
      >
        {loading ? <Spin block /> : <ChangeModeContent modeMap={modeMap} cb={() => setVisible(false)} />}
      </Modal>
    </>
  );
}

/**
 * 导航栏组件
 * 负责渲染应用的顶部导航栏，根据不同的模块和模式展示不同的功能组件。
 */
export default function Index({ menus = undefined, style = {} }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const routeScope = getRouteScope();
  const currentModule = getModule();
  const leftMenu = useLeftMenu();
  const menu = useMenu();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navTitle = useMemo(() => {
    if (!routeScope) {
      return '';
    }
    const consoleTitleKey = CONSOLE_TITLE_KEYS[currentModule];
    if (consoleTitleKey) {
      return t(consoleTitleKey);
    }
    const title = leftMenu?.children?.length ? leftMenu?.title : '';
    return title ? NAV_MAP[title]?.title || title : '';
  }, [currentModule, leftMenu, routeScope, t]);

  const isClusterModule = currentModule === 'cluster' || currentModule === 'wsp';

  const shouldShowChangeModule =
    currentModule === 'data-center' || currentModule === 'console' || isClusterModule || currentModule === 'product';

  /** 移动端动态菜单数据 */
  const mobileNavItems = useMemo(() => {
    const items = menus || menu;
    if (!items || !Array.isArray(items)) return [];
    return items.filter((i: any) => !!i.children?.length || !!i.path);
  }, [menu, menus]);

  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);

  return (
    <div className={`${styles['navbar-container']} ${pathname === '/home' ? styles.homeNavbar : ''}`} data-top-nav="true" style={style}>
      {/* 导航栏左侧内容 */}
      <div className={styles.nav_left}>
        <Logo />
        {navTitle && <div className={styles.nav_title}>{navTitle}</div>}
        <DropMenu menus={menus} />
      </div>

      {/* 导航栏右侧（桌面端） */}
      <div className={styles.nav_right}>
        {shouldShowChangeModule && <ChangeModule />}
        <ChangeModeModal />
        <DocBtn />
        <Explore />
        <SelectLang />
        <User />
      </div>

      {/* 移动端图标工具栏 */}
      <div className={styles.mobileToolbar}>
        <MobileConsoleBtn />
        <MobileDocBtn />
        <MobileExploreBtn />
        <MobileLangBtn />
        <MobileIconBtn icon={<IconMenu />} label={t('common:feature.navigation')} onClick={() => setMobileMenuOpen(true)} />
      </div>

      {/* 移动端动态菜单抽屉 */}
      <Drawer
        className={styles.mobileDrawer}
        visible={mobileMenuOpen}
        placement="right"
        width={280}
        footer={null}
        onCancel={closeMobileMenu}
        title={t('common:feature.navigation')}
        closable
      >
        {mobileNavItems.length > 0 && (
          <div className={styles.mobileNavSection}>
            {mobileNavItems.map((item: any, idx: number) => (
              <MobileNavItem
                key={item.id || item.title || idx}
                title={NAV_MAP[item.title]?.title || item.title || item.name || t('common:feature.navigation')}
                path={getMenuItemPath(item)}
                items={item.children}
                onClose={closeMobileMenu}
              />
            ))}
          </div>
        )}
      </Drawer>
    </div>
  );
}
